import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineInformationCircle, HiPuzzle } from "react-icons/hi";
import { SiAsana, SiJira, SiMicrosoftazure } from "react-icons/si";
import { Drawer } from "./index";
import ImportFromAzureModal from "./ImportIntegration/ImportFromAzureModal";
import ImportFromJiraModal from "./ImportIntegration/ImportFromJiraModal";
import { useFlags } from "launchdarkly-react-client-sdk";
import useIntegrationStatus from "../../hooks/useIntegrationStatus";

interface ImportDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    initialTab?: 'import' | 'states' | 'sprints';
    initialIntegration?: IntegrationType;
    openManageProjects?: boolean;
}
type IntegrationType = 'azure' | 'jira' | 'asana' | 'monday';

const ImportDrawer = ({ isOpen, onClose, initialTab, initialIntegration, openManageProjects }: ImportDrawerProps) => {
    const navigate = useNavigate();
    const {
        asanaImportLevelFive = false,
        mondayImportLevelFive = false,
    } = useFlags();
    const [integrationType, setIntegrationType] = useState<IntegrationType | null>(null);
    const { isIntegrationActive } = useIntegrationStatus();

    const jiraActive = isIntegrationActive('jira');
    const azureActive = isIntegrationActive('azure');

    useEffect(() => {
        if (!isOpen) return;

        if (initialIntegration) {
            setIntegrationType(initialIntegration);
        } else if (jiraActive && !azureActive) {
            setIntegrationType('jira');
        } else if (azureActive && !jiraActive) {
            setIntegrationType('azure');
        }
    }, [isOpen, jiraActive, azureActive, initialIntegration]);

    const handleImportSourceClick = (source: IntegrationType) => {
        setIntegrationType(source);
    };

    const handleCloseDrawer = () => {
        setIntegrationType(null);
        onClose();
    };

    const handleImportModalClose = () => {
        setIntegrationType(null);
        handleCloseDrawer();
    };

    const handleBackToDrawer = () => {
        if (jiraActive && azureActive) {
            setIntegrationType(null);
        } else {
            handleCloseDrawer();
        }
    };

    const shouldShowSelectionDrawer = (jiraActive && azureActive) || (!jiraActive && !azureActive);

    return (
        <>
            <Drawer
                isOpen={isOpen && shouldShowSelectionDrawer}
                onClose={handleCloseDrawer}
                position="right"
                className="w-full sm:w-[480px]"
            >
                <div className="relative h-[calc(100%-55px)] overflow-auto flex flex-col">
                    <button
                        type="button"
                        className="ml-auto absolute right-4 top-4 w-8 h-8 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white text-sm z-[999] flex items-center justify-center transition-all duration-200 hover:scale-105 shadow-sm hover:shadow-md group"
                        onClick={handleCloseDrawer}
                        title="Close"
                    >
                        <svg
                            className="w-4 h-4 "
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>

                    <div className="px-6 py-4">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                            Import Project
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                            Select an active integration to manage projects and settings
                        </p>

                        {/* Integration credentials message */}
                        {/*<div className="mb-6 p-4 bg-primary-light/10 dark:bg-primary-dark/20 border border-primary-light/30 dark:border-primary-dark/50 rounded-lg flex items-start">*/}
                        {/*    <HiOutlineInformationCircle className="h-5 w-5 text-primary-light flex-shrink-0 mt-0.5 mr-3" />*/}
                        {/*    <p className="text-sm text-primary-light dark:text-primary-dark">*/}
                        {/*        If you don't see your integration here, please go to Settings and add your credentials to activate the integration.*/}
                        {/*    </p>*/}
                        {/*</div>*/}

                        <div className="grid grid-cols-1 gap-4 mt-4">
                            {isIntegrationActive('jira') && (
                                <div
                                    className={`bg-secondary-light dark:bg-secondary-dark rounded-lg border p-4 flex items-center cursor-pointer hover:bg-hover-light dark:hover:bg-hover-dark transition-colors ${integrationType === "jira" ? "border-primary-light ring-2 ring-primary-light/30 dark:ring-primary-dark/50" : "border-gray-200 dark:border-gray-700"}`}
                                    onClick={() => handleImportSourceClick("jira")}
                                >
                                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-light/10 dark:bg-primary-dark/20 rounded-full">
                                        <SiJira className="h-7 w-7 text-primary-light" />
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-md font-medium text-gray-900 dark:text-white">Jira</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Manage Jira projects</p>
                                    </div>
                                </div>
                            )}

                            {asanaImportLevelFive && isIntegrationActive('asana') && (
                                <div
                                    className={`bg-secondary-light dark:bg-secondary-dark rounded-lg border p-4 flex items-center cursor-pointer hover:bg-hover-light dark:hover:bg-hover-dark transition-colors ${integrationType === "asana" ? "border-red-500 ring-2 ring-red-200 dark:ring-red-900" : "border-gray-200 dark:border-gray-700"}`}
                                    onClick={() => handleImportSourceClick("asana")}
                                >
                                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-red-50 dark:bg-red-900/30 rounded-full">
                                        <SiAsana className="h-7 w-7 text-red-500" />
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-md font-medium text-gray-900 dark:text-white">Asana</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Manage Asana projects</p>
                                    </div>
                                </div>
                            )}

                            {isIntegrationActive('azure') && (
                                <div
                                    className={`bg-secondary-light dark:bg-secondary-dark rounded-lg border p-4 flex items-center cursor-pointer hover:bg-hover-light dark:hover:bg-hover-dark transition-colors ${integrationType === "azure" ? "border-primary-dark ring-2 ring-primary-light/30 dark:ring-primary-dark/50" : "border-gray-200 dark:border-gray-700"}`}
                                    onClick={() => handleImportSourceClick("azure")}
                                >
                                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary-light/10 dark:bg-primary-dark/20 rounded-full">
                                        <SiMicrosoftazure className="h-7 w-7 text-primary-dark" />
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-md font-medium text-gray-900 dark:text-white">Azure DevOps</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Manage Azure DevOps projects</p>
                                    </div>
                                </div>
                            )}

                            {mondayImportLevelFive && isIntegrationActive('monday') && (
                                <div
                                    className={`bg-secondary-light dark:bg-secondary-dark rounded-lg border p-4 flex items-center cursor-pointer hover:bg-hover-light dark:hover:bg-hover-dark transition-colors ${integrationType === "monday" ? "border-yellow-500 ring-2 ring-yellow-200 dark:ring-yellow-900" : "border-gray-200 dark:border-gray-700"}`}
                                    onClick={() => handleImportSourceClick("monday")}
                                >
                                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-yellow-50 dark:bg-yellow-900/30 rounded-full">
                                        <svg viewBox="0 0 24 24" width="26" height="26" fill="#ff3d57" className="h-7 w-7">
                                            <path d="M9.45 6a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9zm8.55 0a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9zM3.45 15.9a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9zM12 15.9a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9zm8.55 0a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9zm-14.55 9a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9zm8.55 0a3.45 3.45 0 1 0 0-6.9 3.45 3.45 0 0 0 0 6.9z" />
                                        </svg>
                                    </div>
                                    <div className="ml-4">
                                        <h3 className="text-md font-medium text-gray-900 dark:text-white">Monday</h3>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">Manage Monday projects</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {!(
                            (isIntegrationActive('jira')) ||
                            (asanaImportLevelFive && isIntegrationActive('asana')) ||
                            (isIntegrationActive('azure')) ||
                            (mondayImportLevelFive && isIntegrationActive('monday'))
                        ) && (
                                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-6 text-center">
                                    <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                                        <HiOutlineInformationCircle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                                    </div>
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                                        No Active Integrations
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                        No integrations are currently active in this organization. Please set up an integration first to manage and import projects.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            handleCloseDrawer();
                                            navigate('/settings#Integrations');
                                        }}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-light hover:bg-primary-hover rounded-lg transition-colors shadow-sm hover:shadow-md"
                                    >
                                        <HiPuzzle className="w-4 h-4" />
                                        Go to Integration Settings
                                    </button>
                                </div>
                            )}
                    </div>
                </div>
            </Drawer>

            <ImportFromAzureModal
                showModal={integrationType === "azure"}
                closeModal={handleImportModalClose}
                onImportSuccess={handleImportModalClose}
                onBack={handleBackToDrawer}
                initialTab={initialTab}
                openManageProjects={openManageProjects}
            />

            <ImportFromJiraModal
                showModal={integrationType === "jira"}
                closeModal={handleImportModalClose}
                onImportSuccess={handleImportModalClose}
                onBack={handleBackToDrawer}
                initialTab={initialTab}
            />
        </>
    );
};

export default ImportDrawer;